import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Grid, Slider } from '@mui/material';
import {
  BarChart,
  Bar,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import AIOverviewCard from 'src/App/Dashboards/Market/Components/Cards/AIOverviewCard';
import { LoadingCard } from 'src/App/Dashboards/Market/Components/Cards';
import NoDataCard from 'src/App/Dashboards/Market/Components/Cards/NoDataCard';
import LockIcon from '@mui/icons-material/Lock';
import {
  COLOR_PRIMARY_2,
  COLOR_TEXT_2,
  COLOR_GREY_200,
  COLOR_SECONDARY_1,
  COLOR_PRIMARY_2_400,
} from 'src/utils/colors';

const CHART_HEIGHT = 280;
const BAR_COLOR = '#0097B2';
/** Lighter shade for bars below average (By day of week). */
const BAR_COLOR_BELOW_AVG = COLOR_PRIMARY_2_400;

const CALENDAR_FROM_DATE = '2025-01-01';

const DAY_NAMES: Array<{ day: string; dayShort: string }> = [
  { day: 'Monday', dayShort: 'Mon' },
  { day: 'Tuesday', dayShort: 'Tue' },
  { day: 'Wednesday', dayShort: 'Wed' },
  { day: 'Thursday', dayShort: 'Thu' },
  { day: 'Friday', dayShort: 'Fri' },
  { day: 'Saturday', dayShort: 'Sat' },
  { day: 'Sunday', dayShort: 'Sun' },
];

function addDaysToDateStr(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// Same data source as "How do events and holidays affect demand?" (cachedMarketData.historical_occupancy_calendar)
// Backend returns already-adjusted occupancy (weekday + last-minute booking). From 1 Jan 2025 only.
// Fills all days from 2025-01-01 to latest available day; missing days get value 0.
function buildByDayDataFromCalendar(cachedMarketData: any): Array<{ date: string; dateLabel: string; dayOfWeek: string; dayShort: string; value: number }> {
  const historical = cachedMarketData?.data?.historical_occupancy_calendar ?? {};
  const transform = (data: Record<string, { occupancy?: number }>) =>
    Object.entries(data).reduce((acc, [date, info]) => {
      const val = info?.occupancy;
      if (val != null && !Number.isNaN(Number(val))) acc[date] = Number(val);
      return acc;
    }, {} as Record<string, number>);
  const combined = transform(historical);
  const sorted = Object.entries(combined)
    .filter(([date]) => date >= CALENDAR_FROM_DATE)
    .map(([date, value]) => ({
      date,
      dateLabel: date,
      value,
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const getDayInfo = (d: Date) => DAY_NAMES[(d.getDay() + 6) % 7];

  const buildRow = (date: string, value: number) => {
    const d = new Date(date + 'T12:00:00');
    const { day, dayShort } = getDayInfo(d);
    return {
      date,
      dateLabel: d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: '2-digit' }),
      dayOfWeek: day,
      dayShort,
      value,
    };
  };

  const dataByDate = new Map<string, ReturnType<typeof buildRow>>();
  for (const { date, value } of sorted) {
    dataByDate.set(date, buildRow(date, value));
  }

  const maxDate = sorted.length > 0 ? sorted[sorted.length - 1].date : CALENDAR_FROM_DATE;
  const result: Array<ReturnType<typeof buildRow>> = [];
  let current = CALENDAR_FROM_DATE;
  while (current <= maxDate) {
    const row = dataByDate.get(current) ?? buildRow(current, 0);
    result.push(row);
    current = addDaysToDateStr(current, 1);
  }
  return result;
}

function aggregateByDayOfWeek(
  byDayData: Array<{ date: string; value: number }>
): Array<{ day: string; dayShort: string; value: number }> {
  const groups = new Map<number, number[]>();
  for (let i = 0; i < 7; i++) groups.set(i, []);
  for (const { date, value } of byDayData) {
    if (value === 0) continue; // skip zeros so they don't pull down the average
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) continue;
    const dayIndex = (d.getDay() + 6) % 7;
    groups.get(dayIndex)!.push(value);
  }
  return DAY_NAMES.map(({ day, dayShort }, i) => {
    const vals = groups.get(i) ?? [];
    const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    return { day, dayShort, value: Math.round(avg * 10) / 10 };
  });
}

function getMondayKey(dateStr: string): string {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const day = d.getDay();
  const daysSinceMonday = (day + 6) % 7;
  const monday = new Date(d);
  monday.setDate(d.getDate() - daysSinceMonday);
  return monday.toISOString().slice(0, 10);
}

function aggregateByWeek(
  byDayData: Array<{ date: string; value: number }>
): Array<{ week: string; weekIndex: number; value: number }> {
  const groups = new Map<string, number[]>();
  for (const { date, value } of byDayData) {
    const key = getMondayKey(date);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(value);
  }
  // All weeks from week containing 2025-01-01 (Monday 2024-12-30) to week containing latest day
  const firstWeekMonday2025 = '2024-12-30';
  const maxDate = byDayData.length > 0 ? byDayData[byDayData.length - 1].date : firstWeekMonday2025;
  const lastMonday = getMondayKey(maxDate);
  const result: Array<{ week: string; weekIndex: number; value: number }> = [];
  let weekIndex = 0;
  let currentMonday = firstWeekMonday2025;
  while (currentMonday <= lastMonday) {
    const vals = groups.get(currentMonday) ?? [];
    const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    const mondayLabel = new Date(currentMonday + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
    result.push({ week: mondayLabel, weekIndex, value: Math.round(avg * 10) / 10 });
    weekIndex += 1;
    currentMonday = addDaysToDateStr(currentMonday, 7);
  }
  return result;
}

// Mock: future pacing (next 90 days)
const FUTURE_PACING = Array.from({ length: 13 }, (_, i) => {
  const dayOffset = i * 7;
  return { date: `+${dayOffset}d`, dayOffset, value: 40 + (dayOffset / 90) * 45 + Math.random() * 10 };
});

// Mock: booking length 1, 2, 3, ... 14, 15+
const BOOKING_LENGTH_LABELS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15+'];
const BOOKING_LENGTH_DATA = BOOKING_LENGTH_LABELS.map((label, i) => ({
  length: label,
  count: i === 0 ? 420 : i < 5 ? 380 - i * 40 : i < 10 ? 200 - (i - 5) * 15 : i < 14 ? 130 - (i - 10) * 10 : 95,
}));

const axisTickStyle = {
  fontSize: 12,
  fontFamily: 'Roboto',
  fill: COLOR_TEXT_2,
  fontWeight: 500,
};

/** Shown on hover when a data point value is 0 (data still being processed). */
const ZERO_VALUE_TOOLTIP_MESSAGE =
  "These new data points are currently being processed. It'll be available by 2nd week of March.";

const SimpleTooltip = ({
  active,
  payload,
  labelKey = 'date',
  valueKey = 'value',
  suffix = '',
  zeroValueMessage,
  isUserAllowed: allowed = true,
}: any) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  if (!allowed) {
    return (
      <Box sx={{ background: '#fff', p: 1.5, borderRadius: 2, boxShadow: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LockIcon sx={{ fontSize: '1rem', color: COLOR_TEXT_2 }} />
        <Typography variant="body2" fontWeight={600} color={COLOR_TEXT_2}>
          {p[labelKey]}
        </Typography>
      </Box>
    );
  }
  const value = p[valueKey];
  const isZero = value === 0 || value === null || value === undefined;
  if (isZero && zeroValueMessage) {
    return (
      <Box sx={{ background: '#fff', p: 1.5, borderRadius: 2, boxShadow: 2, maxWidth: 260 }}>
        <Typography variant="body2" fontWeight={600} color={COLOR_TEXT_2}>
          {p[labelKey]}: {value}{suffix}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {zeroValueMessage}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ background: '#fff', p: 1.5, borderRadius: 2, boxShadow: 2 }}>
      <Typography variant="body2" fontWeight={600} color={COLOR_TEXT_2}>
        {p[labelKey]}: {p[valueKey]}{suffix}
      </Typography>
    </Box>
  );
};

const ByDayTooltip = ({ active, payload, isUserAllowed: allowed = true }: any) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  if (!allowed) {
    return (
      <Box sx={{ background: '#fff', p: 1.5, borderRadius: 2, boxShadow: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LockIcon sx={{ fontSize: '1rem', color: COLOR_TEXT_2 }} />
        <Typography variant="body2" fontWeight={600} color={COLOR_TEXT_2}>
          {p.dateLabel} ({p.dayOfWeek})
        </Typography>
      </Box>
    );
  }
  const isZero = p.value === 0 || p.value === null || p.value === undefined;
  return (
    <Box sx={{ background: '#fff', p: 1.5, borderRadius: 2, boxShadow: 2, maxWidth: 260 }}>
      <Typography variant="body2" fontWeight={600} color={COLOR_TEXT_2}>
        {p.dateLabel} ({p.dayOfWeek})
      </Typography>
      <Typography variant="body2" color={COLOR_TEXT_2}>
        Occupancy: {p.value}%
      </Typography>
      {isZero && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {ZERO_VALUE_TOOLTIP_MESSAGE}
        </Typography>
      )}
    </Box>
  );
};

/** Margins used when showing blurred chart (leave space for axes so overlay only covers plot). */
const BLUR_CHART_MARGIN = { top: 24, right: 24, left: 50, bottom: 30 };

const blurOverlaySx = {
  position: 'absolute' as const,
  top: BLUR_CHART_MARGIN.top,
  left: BLUR_CHART_MARGIN.left,
  right: BLUR_CHART_MARGIN.right,
  bottom: BLUR_CHART_MARGIN.bottom,
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  pointerEvents: 'none' as const,
};

/** Mock by-day data when isUserAllowed is false: ~90 days from 2025-01-01 with fake occupancy. */
function buildMockByDayData(): Array<{ date: string; dateLabel: string; dayOfWeek: string; dayShort: string; value: number }> {
  const result: Array<{ date: string; dateLabel: string; dayOfWeek: string; dayShort: string; value: number }> = [];
  const len = 90;
  for (let i = 0; i < len; i++) {
    const date = addDaysToDateStr(CALENDAR_FROM_DATE, i);
    const d = new Date(date + 'T12:00:00');
    const dayIndex = (d.getDay() + 6) % 7;
    const { day, dayShort } = DAY_NAMES[dayIndex];
    const value = 52 + Math.sin((i / len) * Math.PI * 2) * 12 + (dayIndex === 5 || dayIndex === 6 ? 8 : 0);
    result.push({
      date,
      dateLabel: d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: '2-digit' }),
      dayOfWeek: day,
      dayShort,
      value: Math.round(Math.max(0, Math.min(100, value)) * 10) / 10,
    });
  }
  return result;
}

export interface BookingPatternChartsProps {
  isMobileView?: boolean;
  /** When true, use full/half layout and hide Future pacing + Booking length (for Market Intelligence). */
  useMarketIntelligenceLayout?: boolean;
  /** Cached market data; historical_occupancy_calendar is used for "By day" chart (same as DailyOccupancyRateCalendar). */
  cachedMarketData?: {
    loading?: boolean;
    data?: {
      historical_occupancy_calendar?: Record<string, { occupancy?: number }>;
    };
  };
  /** When false (is_user_allowed from API), show blurred charts with mock data and clear axes. */
  isUserAllowed?: boolean;
}

const BookingPatternCharts: React.FC<BookingPatternChartsProps> = (props) => {
  const { isMobileView = false, useMarketIntelligenceLayout = false, cachedMarketData, isUserAllowed = true } = props;
  const useHalfRow = false; // By day of week and By week: always full row
  const { byDayData, isUsingCalendarData } = useMemo(() => {
    if (!isUserAllowed) {
      const mock = buildMockByDayData();
      return { byDayData: mock, isUsingCalendarData: true };
    }
    const fromCalendar = buildByDayDataFromCalendar(cachedMarketData);
    return {
      byDayData: fromCalendar,
      isUsingCalendarData: fromCalendar.length > 0,
    };
  }, [cachedMarketData, isUserAllowed]);

  const byDayOfWeekData = useMemo(
    () => (isUsingCalendarData ? aggregateByDayOfWeek(byDayData) : []),
    [byDayData, isUsingCalendarData]
  );

  const byDayOfWeekAverage = useMemo(() => {
    if (!byDayOfWeekData.length) return 0;
    const sum = byDayOfWeekData.reduce((acc, d) => acc + d.value, 0);
    return sum / byDayOfWeekData.length;
  }, [byDayOfWeekData]);

  const byWeekData = useMemo(
    () => (isUsingCalendarData ? aggregateByWeek(byDayData) : []),
    [byDayData, isUsingCalendarData]
  );

  const isLoadingCalendar = Boolean(cachedMarketData?.loading && !isUsingCalendarData);

  const byDayAverage = useMemo(() => {
    if (!byDayData.length) return 0;
    const sum = byDayData.reduce((acc, d) => acc + d.value, 0);
    return Math.round((sum / byDayData.length) * 10) / 10;
  }, [byDayData]);

  const [brushRange, setBrushRange] = useState({ startIndex: 0, endIndex: 0 });
  useEffect(() => {
    if (byDayData.length > 0) {
      setBrushRange({ startIndex: 0, endIndex: byDayData.length - 1 });
    }
  }, [byDayData.length]);

  const byDayDataInRange = useMemo(() => {
    const { startIndex, endIndex } = brushRange;
    if (byDayData.length === 0) return [];
    const start = Math.max(0, Math.min(startIndex, byDayData.length - 1));
    const end = Math.max(start, Math.min(endIndex, byDayData.length - 1));
    return byDayData.slice(start, end + 1);
  }, [byDayData, brushRange]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* 1. By day from 1 Jan 2025 - same data as "How do events and holidays affect demand?" */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, fontSize: '1rem', mb: 1.5 }}>
          By day (from 1 Jan 2025)
        </Typography>
        {isUsingCalendarData ? (
          <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
            <Box sx={{ width: '100%', p: 2 }}>
              <Box sx={{ position: 'relative', width: '100%', height: CHART_HEIGHT }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={byDayDataInRange}
                    margin={isUserAllowed ? { top: 24, right: 24, left: 0, bottom: 20 } : BLUR_CHART_MARGIN}
                    barCategoryGap={byDayDataInRange.length > 60 ? '1%' : '2%'}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
                    <XAxis dataKey="dateLabel" axisLine={false} tickLine={false} tick={axisTickStyle} interval="preserveStartEnd" />
                    <YAxis domain={[0, 100]} tickLine={false} axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }} tick={axisTickStyle} />
                    <Tooltip content={<ByDayTooltip isUserAllowed={isUserAllowed} />} cursor={false} />
                    <Bar dataKey="value" fill={BAR_COLOR} radius={[4, 4, 0, 0]} barSize={byDayDataInRange.length > 60 ? 4 : 6} maxBarSize={byDayDataInRange.length > 60 ? 8 : 10} />
                    <ReferenceLine
                      y={byDayAverage}
                      stroke={COLOR_SECONDARY_1}
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      label={{
                        value: 'Average',
                        position: 'top',
                        fill: COLOR_SECONDARY_1,
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
                {!isUserAllowed && <Box sx={blurOverlaySx} />}
              </Box>
              {byDayData.length > 1 && (
                <Box sx={{ px: 0.5, pt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Date range: {byDayDataInRange[0]?.dateLabel ?? ''} – {byDayDataInRange[byDayDataInRange.length - 1]?.dateLabel ?? ''}
                  </Typography>
                  <Slider
                    value={[brushRange.startIndex, brushRange.endIndex]}
                    min={0}
                    max={byDayData.length - 1}
                    step={1}
                    onChange={(_, value) => {
                      const [a, b] = value as number[];
                      setBrushRange({ startIndex: a, endIndex: b });
                    }}
                    valueLabelDisplay="off"
                    sx={{ color: COLOR_PRIMARY_2 }}
                  />
                </Box>
              )}
            </Box>
          </AIOverviewCard>
        ) : isLoadingCalendar ? (
          <LoadingCard height={CHART_HEIGHT} type="bars" />
        ) : (
          <NoDataCard height={CHART_HEIGHT} borderRadius={8} />
        )}
      </Box>

      {/* 2 & 3. By day of week + By week: half-width row on desktop when useMarketIntelligenceLayout */}
      {useHalfRow ? (
        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, fontSize: '1rem', mb: 1.5 }}>
              By day of week
            </Typography>
            {isUsingCalendarData ? (
              <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
                <Box sx={{ width: '100%', height: CHART_HEIGHT, p: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byDayOfWeekData} margin={{ top: 24, right: 24, left: 0, bottom: 20 }} barCategoryGap="25%">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
                      <XAxis dataKey="dayShort" axisLine={false} tickLine={false} tick={axisTickStyle} />
                      <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }} tick={axisTickStyle} />
                      <Tooltip content={<SimpleTooltip labelKey="day" valueKey="value" suffix="%" zeroValueMessage={ZERO_VALUE_TOOLTIP_MESSAGE} isUserAllowed={isUserAllowed} />} cursor={false} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={32} maxBarSize={48}>
                        {byDayOfWeekData.map((entry, index) => (
                          <Cell key={`dow-${index}`} fill={entry.value < byDayOfWeekAverage ? BAR_COLOR_BELOW_AVG : BAR_COLOR} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </AIOverviewCard>
            ) : isLoadingCalendar ? (
              <LoadingCard height={CHART_HEIGHT} type="bars" />
            ) : (
              <NoDataCard height={CHART_HEIGHT} borderRadius={8} />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, fontSize: '1rem', mb: 1.5 }}>
              By week (from 1 Jan 2025)
            </Typography>
            {isUsingCalendarData ? (
              <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
                <Box sx={{ width: '100%', height: CHART_HEIGHT, p: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byWeekData} margin={{ top: 24, right: 24, left: 0, bottom: 20 }} barCategoryGap="2%">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
                      <XAxis dataKey="week" axisLine={false} tickLine={false} tick={axisTickStyle} interval={5} />
                      <YAxis tickLine={false} axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }} tick={axisTickStyle} />
                      <Tooltip content={<SimpleTooltip labelKey="week" valueKey="value" suffix="%" zeroValueMessage={ZERO_VALUE_TOOLTIP_MESSAGE} isUserAllowed={isUserAllowed} />} cursor={false} />
                      <Bar dataKey="value" fill={BAR_COLOR} radius={[4, 4, 0, 0]} barSize={8} maxBarSize={12} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </AIOverviewCard>
            ) : isLoadingCalendar ? (
              <LoadingCard height={CHART_HEIGHT} type="bars" />
            ) : (
              <NoDataCard height={CHART_HEIGHT} borderRadius={8} />
            )}
          </Grid>
        </Grid>
      ) : (
        <>
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, fontSize: '1rem', mb: 1.5 }}>
              By day of week
            </Typography>
            {isUsingCalendarData ? (
              <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
                <Box sx={{ width: '100%', height: CHART_HEIGHT, p: 2 }}>
                  <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={byDayOfWeekData} margin={isUserAllowed ? { top: 24, right: 24, left: 0, bottom: 20 } : BLUR_CHART_MARGIN} barCategoryGap="25%">
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
                        <XAxis dataKey="dayShort" axisLine={false} tickLine={false} tick={axisTickStyle} />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }} tick={axisTickStyle} />
                        <Tooltip content={<SimpleTooltip labelKey="day" valueKey="value" suffix="%" zeroValueMessage={ZERO_VALUE_TOOLTIP_MESSAGE} isUserAllowed={isUserAllowed} />} cursor={false} />
                        <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={32} maxBarSize={48}>
                          {byDayOfWeekData.map((entry, index) => (
                            <Cell key={`dow-full-${index}`} fill={entry.value < byDayOfWeekAverage ? BAR_COLOR_BELOW_AVG : BAR_COLOR} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    {!isUserAllowed && <Box sx={blurOverlaySx} />}
                  </Box>
                </Box>
              </AIOverviewCard>
            ) : isLoadingCalendar ? (
              <LoadingCard height={CHART_HEIGHT} type="bars" />
            ) : (
              <NoDataCard height={CHART_HEIGHT} borderRadius={8} />
            )}
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, fontSize: '1rem', mb: 1.5 }}>
              By week (from 1 Jan 2025)
            </Typography>
            {isUsingCalendarData ? (
              <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
                <Box sx={{ width: '100%', height: CHART_HEIGHT, p: 2 }}>
                  <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={byWeekData} margin={isUserAllowed ? { top: 24, right: 24, left: 0, bottom: 20 } : BLUR_CHART_MARGIN} barCategoryGap="2%">
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
                        <XAxis dataKey="week" axisLine={false} tickLine={false} tick={axisTickStyle} interval={5} />
                        <YAxis tickLine={false} axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }} tick={axisTickStyle} />
                        <Tooltip content={<SimpleTooltip labelKey="week" valueKey="value" suffix="%" zeroValueMessage={ZERO_VALUE_TOOLTIP_MESSAGE} isUserAllowed={isUserAllowed} />} cursor={false} />
                        <Bar dataKey="value" fill={BAR_COLOR} radius={[4, 4, 0, 0]} barSize={8} maxBarSize={12} />
                      </BarChart>
                    </ResponsiveContainer>
                    {!isUserAllowed && <Box sx={blurOverlaySx} />}
                  </Box>
                </Box>
              </AIOverviewCard>
            ) : isLoadingCalendar ? (
              <LoadingCard height={CHART_HEIGHT} type="bars" />
            ) : (
              <NoDataCard height={CHART_HEIGHT} borderRadius={8} />
            )}
          </Box>
        </>
      )}

      {/* 4. Future pacing - hidden in Market Intelligence layout */}
      {!useMarketIntelligenceLayout && (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, fontSize: '1rem', mb: 1.5 }}>
          Future pacing
        </Typography>
        <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
          <Box sx={{ width: '100%', height: CHART_HEIGHT, p: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={FUTURE_PACING} margin={{ top: 24, right: 24, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={axisTickStyle} />
                <YAxis domain={[0, 100]} tickLine={false} axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }} tick={axisTickStyle} />
                <Tooltip content={<SimpleTooltip labelKey="date" valueKey="value" suffix="%" />} cursor={false} />
                <Line type="monotone" dataKey="value" stroke={BAR_COLOR} strokeWidth={2} dot={{ r: 4 }} name="Pacing" />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </AIOverviewCard>
      </Box>
      )}

      {/* 5. Booking length - hidden in Market Intelligence layout */}
      {!useMarketIntelligenceLayout && (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, fontSize: '1rem', mb: 1.5 }}>
          Booking length (nights)
        </Typography>
        <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
          <Box sx={{ width: '100%', height: CHART_HEIGHT, p: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={BOOKING_LENGTH_DATA} margin={{ top: 24, right: 24, left: 0, bottom: 20 }} barCategoryGap="15%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
                <XAxis dataKey="length" axisLine={false} tickLine={false} tick={axisTickStyle} />
                <YAxis tickLine={false} axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }} tick={axisTickStyle} label={{ value: 'Count', angle: -90, position: 'insideLeft', style: { fontSize: 12, fontWeight: 600, fill: COLOR_TEXT_2 } }} />
                <Tooltip content={<SimpleTooltip labelKey="length" valueKey="count" suffix=" stays" />} cursor={false} />
                <Bar dataKey="count" fill={BAR_COLOR} radius={[6, 6, 0, 0]} barSize={24} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </AIOverviewCard>
      </Box>
      )}
    </Box>
  );
};

export default BookingPatternCharts;
